/**
 * Read-only detail for a *created / Ready* concept group, rendered in the
 * project right pane (OR-6126). Mounted from the ``/concept-group/:id`` route
 * (``ProjectConceptGroupViewPage``) once the group is published — while it is a
 * Draft that route renders ``ConceptGroupDraftReview`` instead, so this surface
 * is always the Ready state.
 *
 * Consistency with the Draft review (OR-6288): the SAME
 * {@link ConceptGroupAssetHeader} (title + status chip + icon meta row), the
 * SAME {@link ConceptGroupSourceFileBar}, the SAME tab strip, and the SAME
 * {@link ConceptGroupConceptsPanel} render here. Differences: the status chip is
 * ``Ready``; there is no Save and the source-file bar is always read-only (no
 * switching, even for the owner); and a published group has no rules, so the tab
 * strip shows only the Concepts tab. Fully read-only: no inline rename, no
 * approve. TanStack throughout.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import { Box, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { type PageHeaderBreadcrumb } from '@verantos-dev/shared-components';

import { useConceptGroupDetail } from './api/conceptGroupQueries';
import { ConceptGroupAssetHeader } from './components/ConceptGroupAssetHeader';
import { ConceptGroupConceptsPanel } from './components/ConceptGroupConceptsPanel';
import { ConceptGroupSourceFileBar } from './components/ConceptGroupSourceFileBar';
import { useConceptGroupConceptsTable } from './hooks/useConceptGroupConceptsTable';
import { useConceptGroupMetaItems } from './hooks/useConceptGroupMetaItems';

export interface ConceptGroupDetailViewProps {
  conceptGroupId: string;
  /**
   * Breadcrumb trail rendered above the name (OR-6123 F8). Supplied only by
   * the standalone route mount (`ProjectConceptGroupViewPage`); the in-place
   * preview mount omits it.
   */
  breadcrumbs?: PageHeaderBreadcrumb[];
}

export const ConceptGroupDetailView = ({
  conceptGroupId,
  breadcrumbs,
}: ConceptGroupDetailViewProps) => {
  const { formatMessage } = useIntl();

  const detailQuery = useConceptGroupDetail(conceptGroupId || null);
  const detail = detailQuery.data;
  const table = useConceptGroupConceptsTable({
    conceptGroupId: conceptGroupId || null,
    groupName: detail?.conceptGroupName ?? 'concepts',
  });

  // Owner / domains / last-modified are on the created-group detail (unlike the
  // Draft, which reads owner from the artifact manifest). Feed the SAME shared
  // meta builder so the header row matches the Draft exactly — the concept
  // count lives in the concepts strip below (not the header), also like the Draft.
  const metaItems = useConceptGroupMetaItems({
    domains: detail?.domains ? detail.domains.split(',').map((d) => d.trim()) : [],
    ownerName: detail?.owner ?? undefined,
    lastModifiedIso: detail?.modifiedBy?.transactionDate ?? null,
  });

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ paddingX: 6, paddingY: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="text" width={240} height={32} />
        <Skeleton variant="rectangular" height={80} />
      </Box>
    );
  }

  if (detailQuery.isError || !detail) {
    return (
      <Box data-testid="concept-group-detail-error" sx={{ paddingX: 6, paddingY: 3 }}>
        <Typography variant="subtitle1" color="error">
          {formatMessage({ id: 'epiChat.conceptGroup.detail.loadError' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="concept-group-detail-view"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <ConceptGroupAssetHeader
        id="concept-group-detail-header"
        breadcrumbs={breadcrumbs}
        title={detail.conceptGroupName}
        status={detail.assetStatus ?? undefined}
        titleTestId="concept-group-detail-name"
        statusTestId="concept-group-detail-status-chip"
        metaItems={metaItems}
      />
      {/* Same source-file bar as the Draft, but always read-only: the Ready view
          never allows switching the source file, even for the owner. Clicking the
          filename runs the same download as the concepts grid's download icon. */}
      <ConceptGroupSourceFileBar
        testIdPrefix="concept-group-detail"
        gutter={6}
        sourceFilename={detail.sourceFilename ?? `${detail.conceptGroupName}.xlsx`}
        onDownload={() => void table.handleDownload()}
      />
      {/* Match the Draft's tab strip, but a published group has no rules — so the
          Ready view shows only the Concepts tab. */}
      <Tabs
        value="concepts"
        sx={{ paddingX: 6, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        data-testid="concept-group-detail-tabs"
      >
        <Tab
          value="concepts"
          label={formatMessage({ id: 'epiChat.conceptGroupPreview.tab.concepts' })}
          sx={{
            textTransform: 'uppercase',
            typography: 'body2',
            fontWeight: 500,
            letterSpacing: '0.4px',
          }}
          data-testid="concept-group-detail-tab-concepts"
        />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, paddingX: 6, paddingTop: 2 }}>
        <ConceptGroupConceptsPanel
          model={table}
          testIdPrefix="concept-group-detail"
          conceptCount={detail.conceptCount ?? null}
        />
      </Box>
    </Box>
  );
};
